/**
 * Persist a small, allowlisted slice of the TanStack Query cache across
 * launches, so the dashboard paints last-known numbers instantly and
 * revalidates in the background instead of showing skeletons until ~20
 * requests come back.
 *
 * Why this exists (#1246): warm-cache.ts already makes the *bootstrap* state
 * (profile, company, location, subscription) instant on a returning launch —
 * but everything behind it is TanStack Query, whose cache lives only in
 * memory. So every cold start of the mobile app, and every full reload on
 * web, re-ran the whole dashboard fan-out (fleet cockpit alone is ~15 calls,
 * gated behind a 6-step serial chain) before a single number appeared. The
 * data was usually minutes old and nearly identical; the user still waited
 * for all of it.
 *
 * SAFETY — the same rules as warm-cache.ts, tightened:
 *  - Scoped by BOTH profile id and company id. A different user, or the same
 *    user after a company switch, resolves a different storage key, so a
 *    cross-user or cross-tenant read is a cache miss, never a leak. That
 *    matters because some keys (['notifications']) carry no id at all.
 *  - Allowlisted, not blocklisted. Only the query families the dashboard
 *    reads are written; a new query that holds something sensitive is not
 *    persisted until someone adds it here on purpose.
 *  - Busted by build. A deploy that changes a query's data shape must not
 *    hand a component last week's shape, so the snapshot records the build
 *    it came from and is discarded on mismatch.
 *  - Bounded in age and size. Old snapshots are dropped, and a snapshot too
 *    big to write cheaply is not written at all.
 *  - Never a source of truth. Hydrated data is older than the 30s staleTime,
 *    so every hydrated query still refetches on mount; this only decides
 *    whether the first render shows a skeleton or last-known content.
 *
 * Email logins only — the component that drives this refuses PIN sessions,
 * because a kiosk tablet is shared by a whole store.
 */

import type { DehydratedState, Query } from '@tanstack/react-query';

/** Bump to invalidate every persisted snapshot on every device at once. */
const SCHEMA = 'v1';

export const PERSIST_KEY_PREFIX = `rq-cache:${SCHEMA}:`;

/** A snapshot older than this is discarded rather than painted. */
export const PERSIST_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * Serialized snapshots larger than this are not written. The dashboard slice
 * is typically tens of KB; anything near this means a list query was added to
 * the allowlist that shouldn't have been, and localStorage writes are
 * synchronous on the main thread.
 */
export const PERSIST_MAX_BYTES = 750_000;

/**
 * Query-key roots that are written to disk. These are the families the
 * dashboard and the layout around it render on first paint (see the #1246
 * inventory). Anything not listed stays memory-only.
 */
export const PERSISTED_QUERY_ROOTS: ReadonlySet<string> = new Set([
  // Dashboard body
  'fleet-cockpit',
  'location-cockpit',
  'dashboard-stats',
  'sales-locations',
  'has-sales-data',
  'prime-cost-projection',
  'price-watch',
  'temperature-alerts',
  'checklist-analytics',
  'unified-dashboard-birthday',
  // Layout / gates
  'company-memberships',
  'company-policies',
  'notifications',
]);

export interface PersistScope {
  profileId: string | null | undefined;
  companyId: string | null | undefined;
}

/** `rq-cache:v1:<profileId>:<companyId>`, or null when either id is missing. */
export function persistKey({ profileId, companyId }: PersistScope): string | null {
  if (!profileId || !companyId) return null;
  return `${PERSIST_KEY_PREFIX}${profileId}:${companyId}`;
}

/** Whether a query-key root is one of the allowlisted families. */
export function isPersistedRoot(root: unknown): boolean {
  return typeof root === 'string' && PERSISTED_QUERY_ROOTS.has(root);
}

/**
 * True when `value` survives JSON.stringify → JSON.parse unchanged: plain
 * objects, arrays, strings, finite numbers, booleans and null. A Date comes
 * back a string and a Map comes back `{}`, and a component handed either
 * after a reload would crash or render nonsense — so a query holding one is
 * simply not persisted, rather than persisted wrong.
 */
export function isJsonSafe(value: unknown, depth = 0): boolean {
  if (depth > 40) return false;
  if (value === null) return true;
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return true;
    case 'number':
      return Number.isFinite(value);
    case 'object': {
      if (Array.isArray(value)) return value.every((v) => isJsonSafe(v, depth + 1));
      const proto = Object.getPrototypeOf(value);
      if (proto !== Object.prototype && proto !== null) return false;
      return Object.values(value as Record<string, unknown>).every(
        // An undefined property is dropped by JSON, which reads back the same.
        (v) => v === undefined || isJsonSafe(v, depth + 1),
      );
    }
    default:
      return false;
  }
}

/** Whether a query belongs in the persisted snapshot. */
export function shouldPersistQuery(query: Pick<Query, 'queryKey' | 'state'>): boolean {
  if (query.state.status !== 'success') return false;
  if (query.state.data === undefined) return false;
  if (!isPersistedRoot(query.queryKey[0])) return false;
  return isJsonSafe(query.state.data);
}

interface Snapshot {
  buster: string;
  savedAt: number;
  state: DehydratedState;
}

/**
 * Serialize for storage. Returns null when the snapshot is empty or too
 * large to be worth a synchronous write.
 */
export function serializeSnapshot(state: DehydratedState, buster: string, now: number): string | null {
  if (state.queries.length === 0) return null;
  let raw: string;
  try {
    raw = JSON.stringify({ buster, savedAt: now, state } satisfies Snapshot);
  } catch {
    // Non-serializable data (a Map, a cycle) — skip rather than throw into
    // the query cache's notify loop.
    return null;
  }
  return raw.length > PERSIST_MAX_BYTES ? null : raw;
}

/**
 * Parse a stored snapshot. Returns null — meaning "behave exactly as if
 * nothing was cached" — for anything malformed, from another build, or older
 * than PERSIST_MAX_AGE_MS.
 */
export function parseSnapshot(raw: string | null, buster: string, now: number): DehydratedState | null {
  if (!raw) return null;
  let parsed: Partial<Snapshot>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.buster !== buster) return null;
  if (typeof parsed.savedAt !== 'number' || now - parsed.savedAt > PERSIST_MAX_AGE_MS) return null;
  // A clock that jumped backwards would otherwise keep a snapshot alive forever.
  if (parsed.savedAt > now + 60_000) return null;
  const state = parsed.state;
  if (!state || !Array.isArray(state.queries)) return null;
  // Belt and braces: a snapshot written by an older allowlist must not
  // resurrect a family that has since been removed from it.
  return {
    ...state,
    mutations: [],
    queries: state.queries.filter((q) => Array.isArray(q?.queryKey) && isPersistedRoot(q.queryKey[0])),
  };
}

/**
 * Identify the running build without a build-time define. In production the
 * entry script's filename is content-hashed, and Rollup's hashes cascade up
 * the import graph, so it changes whenever any shipped module changes. The
 * fallback (dev, tests, an unexpected document) is a constant, which is fine
 * there: dev reloads don't cross deploys.
 */
export function currentBuildId(doc: Pick<Document, 'querySelector'> | null = typeof document !== 'undefined' ? document : null): string {
  try {
    const entry = doc?.querySelector<HTMLScriptElement>('script[type="module"][src]');
    const src = entry?.getAttribute('src');
    if (src) return src;
  } catch {
    /* fall through */
  }
  return 'dev';
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

export function readSnapshot(storage: StorageLike | null, key: string, buster: string, now: number): DehydratedState | null {
  if (!storage) return null;
  try {
    return parseSnapshot(storage.getItem(key), buster, now);
  } catch {
    return null;
  }
}

export function writeSnapshot(storage: StorageLike | null, key: string, raw: string | null): void {
  if (!storage) return;
  try {
    if (raw === null) storage.removeItem(key);
    else storage.setItem(key, raw);
  } catch {
    // Quota exceeded / private mode. Drop the stale one so a later read can't
    // paint something older than what failed to write.
    try {
      storage.removeItem(key);
    } catch {
      /* nothing left to do */
    }
  }
}

/**
 * Remove every persisted snapshot on this device. Called on sign-out so a
 * shared browser keeps nothing of the previous user's dashboard.
 */
export function clearAllSnapshots(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith('rq-cache:')) doomed.push(k);
    }
    doomed.forEach((k) => storage.removeItem(k));
  } catch {
    /* best effort */
  }
}
